// src/pages/finance-portal/pay-stubs/PayStubDetail.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, Card, CardContent, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Alert, CircularProgress, Divider, Grid,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const headCellSx = {
    fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
};

const bodyCellSx = {
    fontFamily, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9',
};

function LineTable({ title, lines, showHours }) {
    if (!lines || lines.length === 0) return null;
    return (
        <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 1 }}>{title}</Typography>
            <Card sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell sx={headCellSx}>Description</TableCell>
                                {showHours && <TableCell sx={headCellSx} align="right">Hours</TableCell>}
                                {showHours && <TableCell sx={headCellSx} align="right">Rate</TableCell>}
                                <TableCell sx={headCellSx} align="right">Amount</TableCell>
                                <TableCell sx={headCellSx} align="right">YTD</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {lines.map((line, idx) => (
                                <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                    <TableCell sx={bodyCellSx}>{line.description || line.line_type || '—'}</TableCell>
                                    {showHours && <TableCell sx={bodyCellSx} align="right">{line.hours != null ? line.hours : '—'}</TableCell>}
                                    {showHours && <TableCell sx={bodyCellSx} align="right">{line.rate != null ? formatCurrency(line.rate) : '—'}</TableCell>}
                                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }} align="right">{formatCurrency(line.amount)}</TableCell>
                                    <TableCell sx={bodyCellSx} align="right">{line.ytd_amount != null ? formatCurrency(line.ytd_amount) : '—'}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell colSpan={showHours ? 3 : 1} sx={{ ...bodyCellSx, fontWeight: 700 }}>Total</TableCell>
                                <TableCell sx={{ ...bodyCellSx, fontWeight: 700 }} align="right">
                                    {formatCurrency(lines.reduce((sum, l) => sum + (parseFloat(l.amount) || 0), 0))}
                                </TableCell>
                                <TableCell sx={bodyCellSx} align="right" />
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
        </Box>
    );
}

export default function PayStubDetail() {
    const navigate = useNavigate();
    const { stubId } = useParams();

    const [stub, setStub] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchStub = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(`/api/finance/payroll/pay-stubs/${stubId}`);
            if (res.data.success) {
                setStub(res.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load pay stub');
        } finally {
            setLoading(false);
        }
    }, [stubId]);

    useEffect(() => { fetchStub(); }, [fetchStub]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress sx={{ color: ACCENT }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error" sx={{ borderRadius: '12px', fontFamily, mb: 2 }}>{error}</Alert>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
                    sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Back</Button>
            </Box>
        );
    }

    if (!stub) return null;

    const lines = stub.lines || [];
    const earnings = lines.filter(l => l.line_type === 'EARNING');
    const employeeTaxes = lines.filter(l => l.line_type === 'TAX_EMPLOYEE');
    const pretaxDeductions = lines.filter(l => l.line_type === 'DEDUCTION_PRETAX');
    const posttaxDeductions = lines.filter(l => l.line_type === 'DEDUCTION_POSTTAX');
    const employerTaxes = lines.filter(l => l.line_type === 'TAX_EMPLOYER');
    const employerContributions = lines.filter(l => l.line_type === 'EMPLOYER_CONTRIBUTION');
    const employerLines = [...employerTaxes, ...employerContributions];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
                        sx={{ fontFamily, textTransform: 'none', color: 'rgba(255,255,255,0.8)', mb: 1, '&:hover': { color: '#fff' } }}>
                        Back
                    </Button>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ReceiptLongIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>
                                Pay Stub {stub.stub_number || `#${stub.id}`}
                            </Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                {stub.employee_name || 'Employee'} &middot; Period: {formatDate(stub.period_start)} – {formatDate(stub.period_end)} &middot; Pay Date: {formatDate(stub.pay_date)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1100, mx: 'auto' }}>
                {/* Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {[
                        { label: 'Gross Pay', value: stub.gross_pay, color: '#1e293b' },
                        { label: 'Total Taxes', value: stub.total_employee_taxes, color: '#dc2626' },
                        { label: 'Total Deductions', value: stub.total_deductions, color: '#f59e0b' },
                        { label: 'Net Pay', value: stub.net_pay, color: ACCENT },
                    ].map((item, idx) => (
                        <Grid item xs={6} md={3} key={idx}>
                            <Card sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                                <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', fontWeight: 600, mb: 0.5 }}>{item.label}</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 800, color: item.color }}>{formatCurrency(item.value)}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Line Sections */}
                <LineTable title="Earnings" lines={earnings} showHours />
                <LineTable title="Employee Taxes" lines={employeeTaxes} showHours={false} />
                <LineTable title="Pre-Tax Deductions" lines={pretaxDeductions} showHours={false} />
                <LineTable title="Post-Tax Deductions" lines={posttaxDeductions} showHours={false} />

                {employerLines.length > 0 && (
                    <>
                        <Divider sx={{ my: 3 }} />
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#64748b', mb: 1 }}>Employer Costs (Not deducted from pay)</Typography>
                        <LineTable title="Employer Taxes & Contributions" lines={employerLines} showHours={false} />
                    </>
                )}

                {/* YTD Summary */}
                {stub.ytd_summary && (
                    <Box sx={{ mt: 3 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 1 }}>Year-to-Date Summary</Typography>
                        <Card sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent>
                                <Grid container spacing={2}>
                                    {[
                                        { label: 'YTD Gross', value: stub.ytd_summary.gross_pay },
                                        { label: 'YTD Taxes', value: stub.ytd_summary.total_taxes },
                                        { label: 'YTD Deductions', value: stub.ytd_summary.total_deductions },
                                        { label: 'YTD Net', value: stub.ytd_summary.net_pay },
                                    ].map((item, idx) => (
                                        <Grid item xs={6} md={3} key={idx}>
                                            <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>{item.label}</Typography>
                                            <Typography sx={{ fontFamily, fontSize: '1.15rem', fontWeight: 700, color: '#1e293b' }}>{formatCurrency(item.value)}</Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
